import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { ARTICLES } from "@/data/resources";
import { ogImageMeta } from "@/lib/og-image";
import { leafCanonical } from "@/lib/canonical";

const URL_SELF = "https://vendingmachinefree.com/resources";
const TITLE = "Free Vending Resources — Guides for Facilities & Office Managers";
const DESC =
  "In-depth guides for facilities managers, office managers, and HR: how free vending, smart fridges, and micro-market placement works for 20+ employee offices.";

export const Route = createFileRoute("/resources")({
  head: ({ match, matches }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_SELF },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: leafCanonical(URL_SELF, { match, matches }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Free Vending Resources",
          url: URL_SELF,
          hasPart: ARTICLES.map((a) => ({
            "@type": "Article",
            headline: a.title,
            description: a.description,
            url: `https://vendingmachinefree.com/resources/${a.slug}`,
            datePublished: a.published,
          })),
        }),
      },
    ],
  }),
  component: ResourcesIndex,
});

function ResourcesIndex() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link to="/" hash="contact" className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground">
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <BookOpen className="h-4 w-4" /> Resources
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending Guides for Facilities & Office Managers
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            Straightforward, no-fluff guides on qualifying for free vending, choosing between smart fridges
            and micro-markets, and rolling out breakroom placements at 20+ employee offices.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-5xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ARTICLES.map((a) => (
              <Link
                key={a.slug}
                to="/resources/$slug"
                params={{ slug: a.slug }}
                className="group rounded-xl border border-border bg-card p-6 hover:border-safety hover:shadow-md transition"
              >
                <div className="text-xs uppercase tracking-wider text-safety font-bold">{a.category}</div>
                <h2 className="mt-2 font-display text-lg uppercase group-hover:text-safety">{a.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{a.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.readMinutes} min read</span>
                  <span className="text-safety font-semibold">Read →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
